import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LockOpenOutlinedIcon from '@material-ui/icons/LockOpenOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import useAuth from 'hooks/useAuth';
import * as validators from 'utils/validators';

type ModalAlterarSenhaProps = {
  open: boolean;
  onClose: () => void;
};

const useStyles = makeStyles(({ spacing, typography }) => ({
  textField: {
    marginBottom: spacing(2),
  },
  input: {
    fontSize: typography.pxToRem(14),
  },
}));

export default function ModalAlterarSenha(props: ModalAlterarSenhaProps) {
  const { open, onClose } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [hidePassword, setHidePassword] = useState(true);
  const auth = useAuth();

  const formik = useFormik({
    initialValues: {
      old_password: '',
      new_password: '',
      new_password_confirmation: '',
    },
    validationSchema: validators.alterarSenhaSchema,
    onSubmit: async values => {
      try {
        await auth.alterarSenha(values);
        enqueueSnackbar('A sua senha foi alterada com sucesso.');
        onClose();
      } catch (error) {
        enqueueSnackbar(error as string);
      }
    },
  });

  const handleClose = () => {
    if (onClose && !formik.isSubmitting) onClose();
  };

  const handleToggleHidePassword = () => {
    setHidePassword(!hidePassword);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      TransitionProps={{
        onExited: () => {
          formik.resetForm();
          setHidePassword(true);
        },
      }}
    >
      <DialogHeader
        title="Alteração de senha"
        description="Informe a sua senha atual, a sua nova senha e confirme."
        onClose={handleClose}
      />

      <DialogForm onSubmit={formik.handleSubmit} loading={formik.isSubmitting}>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            id="old_password"
            placeholder="******"
            name="old_password"
            label="Sua senha atual"
            variant="outlined"
            InputLabelProps={{
              className: classes.input,
            }}
            className={classes.textField}
            type={hidePassword ? 'password' : 'text'}
            value={formik.values.old_password}
            onChange={formik.handleChange}
            error={
              formik.touched.old_password && Boolean(formik.errors.old_password)
            }
            helperText={
              formik.touched.old_password && formik.errors.old_password
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    tabIndex={-1}
                    title={hidePassword ? 'Mostrar senha' : 'Ocultar senha'}
                    aria-label={
                      hidePassword ? 'Mostrar senha' : 'Ocultar senha'
                    }
                    aria-disabled={false}
                    onClick={handleToggleHidePassword}
                    onMouseDown={e => e.preventDefault()}
                    size="small"
                    edge="end"
                  >
                    {hidePassword ? (
                      <LockOpenOutlinedIcon color="disabled" />
                    ) : (
                      <LockOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              className: classes.input,
            }}
          />

          <TextField
            fullWidth
            id="new_password"
            placeholder="******"
            name="new_password"
            label="Sua nova senha"
            variant="outlined"
            InputLabelProps={{
              className: classes.input,
            }}
            inputProps={{
              className: classes.input,
            }}
            className={classes.textField}
            type={hidePassword ? 'password' : 'text'}
            value={formik.values.new_password}
            onChange={formik.handleChange}
            error={
              formik.touched.new_password && Boolean(formik.errors.new_password)
            }
            helperText={
              formik.touched.new_password && formik.errors.new_password
            }
          />

          <TextField
            fullWidth
            id="new_password_confirmation"
            placeholder="******"
            name="new_password_confirmation"
            label="Confirmar sua nova senha"
            variant="outlined"
            InputLabelProps={{
              className: classes.input,
            }}
            inputProps={{
              className: classes.input,
            }}
            className={classes.textField}
            type={hidePassword ? 'password' : 'text'}
            value={formik.values.new_password_confirmation}
            onChange={formik.handleChange}
            error={
              formik.touched.new_password_confirmation &&
              Boolean(formik.errors.new_password_confirmation)
            }
            helperText={
              formik.touched.new_password_confirmation &&
              formik.errors.new_password_confirmation
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            disabled={formik.isSubmitting}
            variant="contained"
            color="primary"
            type="submit"
          >
            Pronto
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}
